/**
 * Weight categories, shared by the Assessments page (where they are
 * edited) and the grid (which shows a column's category and its share of the
 * final mark in the header popover).
 *
 * Weight lives on the CATEGORY, never on the assessment. An assessment carries
 * only a `share` of its category, so adding a fourth test doesn't change the
 * fact that Tests are worth 40%. An assessment with no category is "Not
 * counted" - that is how a diagnostic is excluded, with no separate flag.
 */

/*
 * The starting categories a new gradebook gets (Tests 40 / Labs 25 /
 * Projects 25 / Quizzes 10) now live in the BACKEND, which inserts them in the
 * same transaction as the gradebook itself. They are deliberately not a
 * frontend constant: once a gradebook exists it owns its own `categories` rows,
 * which the teacher renames and reweights, so a shared constant here would be a
 * lie the moment anyone edits one. Always read `gradebook.categories`.
 */

/* Slice colours stay inside the brand green, stepping by lightness. */
export const SLICE = [
  "hsl(152 56% 26%)", "hsl(152 62% 36%)", "hsl(152 66% 48%)",
  "hsl(152 70% 62%)", "hsl(152 67% 76%)",
];

/**
 * Assessments as weight rows. `categoryId` is a real column now, so it is taken
 * at face value - `null` means "Not counted" and is a deliberate teacher choice,
 * never a gap to be guessed at. (The old `CATEGORY_BY_TYPE` lookup inferred a
 * category from an assessment's `type` string; it existed only to give the mock
 * seed data plausible categories, and both it and `type` are gone.)
 */
export const toWeightRows = (assessments) =>
  assessments.map((a) => ({
    ...a,
    categoryId: a.categoryId ?? null,
    share: a.share ?? 1,
  }));

/**
 * categoryWeight x (this row's share / all shares in that category), keyed by
 * row id. Rows with no category resolve to 0 - they don't count.
 */
export function effectiveWeights(rows, categories) {
  const shareTotals = {};
  for (const r of rows) {
    if (!r.categoryId) continue;
    shareTotals[r.categoryId] = (shareTotals[r.categoryId] ?? 0) + (Number(r.share) || 0);
  }
  const out = {};
  for (const r of rows) {
    const cat = categories.find((c) => c.id === r.categoryId);
    const total = shareTotals[r.categoryId] ?? 0;
    out[r.id] = !cat || total === 0 ? 0 : (cat.weight * (Number(r.share) || 0)) / total;
  }
  return out;
}
